'use client';

import { useState } from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '../ui/badge';

const expandableBadgeVariants = cva(
  // base classes shared by all variants
  'inline-flex items-center rounded-full border py-0.5 pr-2.5 pl-1 text-xs font-semibold transition-colors',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        error: 'border-transparent bg-[#F26C7442]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

// Single unified type — variant lives here
export interface ExpandableBadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof expandableBadgeVariants> {
  text: string;
  description: string;
}

const iconMap: Record<'error' | 'default', React.ReactNode> = {
  error: <AlertCircle size={14} className="mx-1 text-[#F26C74]" />,
  default: null,
};

const ExpandableBadge = ({
  text,
  description,
  variant = 'error',
  className,
  ...props
}: ExpandableBadgeProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={cn('w-full', className)} {...props}>
      <div className="flex items-center justify-between gap-2">
        <Badge
          className={cn(
            expandableBadgeVariants({ variant }),
            'inline-flex items-center rounded-full border text-body font-normal transition-colors'
          )}
          variant={'outline'}
        >
          {iconMap[variant ?? 'default']}
          <span className='capitalize'>{text}</span>
        </Badge>

        <p
          className={cn(
            'line-clamp-1 text-body-sm text-body-subtle',
            isOpen && 'hidden'
          )}
        >
          {description}
        </p>

        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="shrink-0 text-[#8C9EA1]"
        >
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Full description shown below when expanded */}
      {isOpen && (
        <p className={cn('mt-1 text-body-sm text-body-subtle')}>
          {description}
        </p>
      )}
    </div>
  );
};

export default ExpandableBadge;
